import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { BiometricAuth, BiometryType } from '@aparajita/capacitor-biometric-auth';
import { Preferences } from '@capacitor/preferences';

/**
 * Face ID / Touch ID / fingerprint unlock for the native apps.
 *
 * - `isAvailable()` reports whether the device has usable biometry.
 * - `authenticate()` presents the OS biometric prompt; resolves true on
 *   success, false on cancel/failure (never throws to callers).
 * - The opt-in preference (set once after first login) is persisted with
 *   @capacitor/preferences so the lock gate knows whether to engage.
 *
 * No-op / disabled on web so the web build is unaffected.
 */
@Injectable({ providedIn: 'root' })
export class BiometricService {
  private static readonly ENABLED_KEY = 'biometricEnabled';
  private static readonly PROMPTED_KEY = 'biometricPrompted';

  private get isNative(): boolean {
    return Capacitor.isNativePlatform();
  }

  /** True when the device has enrolled, usable biometry. */
  async isAvailable(): Promise<boolean> {
    if (!this.isNative) {
      return false;
    }
    try {
      const info = await BiometricAuth.checkBiometry();
      return info.isAvailable && info.biometryType !== BiometryType.none;
    } catch {
      return false;
    }
  }

  /** Present the biometric prompt. Resolves true on success, false otherwise. */
  async authenticate(reason = 'Unlock Tourist Tax'): Promise<boolean> {
    if (!this.isNative) {
      return true;
    }
    try {
      await BiometricAuth.authenticate({
        reason,
        cancelTitle: 'Use password',
        allowDeviceCredential: false,
        iosFallbackTitle: 'Use password',
      });
      return true;
    } catch {
      return false;
    }
  }

  /** Whether the user has turned biometric unlock on. */
  async isEnabled(): Promise<boolean> {
    if (!this.isNative) {
      return false;
    }
    const { value } = await Preferences.get({ key: BiometricService.ENABLED_KEY });
    return value === 'true';
  }

  async setEnabled(enabled: boolean): Promise<void> {
    if (!this.isNative) {
      return;
    }
    await Preferences.set({ key: BiometricService.ENABLED_KEY, value: String(enabled) });
  }

  /** Whether we've already shown the one-time opt-in prompt. */
  async hasBeenPrompted(): Promise<boolean> {
    if (!this.isNative) {
      return true;
    }
    const { value } = await Preferences.get({ key: BiometricService.PROMPTED_KEY });
    return value === 'true';
  }

  async markPrompted(): Promise<void> {
    if (!this.isNative) {
      return;
    }
    await Preferences.set({ key: BiometricService.PROMPTED_KEY, value: 'true' });
  }
}
